import apiClient from '@/lib/api-client'
import type { Task } from '@/shared/types/entities'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'

export interface CreateTaskDto {
  projectId: string
  name: string
}

export interface UpdateTaskDto extends Partial<CreateTaskDto> {}

interface TasksResponse {
  tasks: Task[]
}

// Backend has double-wrapped response due to global TransformInterceptor + manual wrapping
interface DoubleWrappedResponse<T> {
  success: boolean
  data: {
    success: boolean
    data: T
  }
}

function getWorkspaceId(): string {
  const { selectedWorkspace } = useWorkspaceStore.getState()
  if (!selectedWorkspace) {
    throw new Error('No workspace selected')
  }
  return selectedWorkspace.id
}

export const taskService = {
  async getAllTasks(search?: string, projectId?: string, includeArchived = false): Promise<Task[]> {
    const workspaceId = getWorkspaceId()
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (projectId) params.projectId = projectId
    if (includeArchived) params.includeArchived = 'true'
    const response = await apiClient.get<DoubleWrappedResponse<TasksResponse>>(`/workspaces/${workspaceId}/tasks`, { params })
    return response.data.data.data.tasks
  },

  async getTasksByProject(projectId: string, includeArchived = false, search?: string): Promise<Task[]> {
    const workspaceId = getWorkspaceId()
    const params: Record<string, string> = { includeArchived: includeArchived.toString() }
    if (search) params.search = search
    const response = await apiClient.get<DoubleWrappedResponse<TasksResponse>>(`/workspaces/${workspaceId}/projects/${projectId}/tasks`, { params })
    return response.data.data.data.tasks
  },

  async createTask(data: CreateTaskDto): Promise<Task> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<DoubleWrappedResponse<Task>>(`/workspaces/${workspaceId}/tasks`, data)
    return response.data.data.data
  },

  async updateTask(id: string, data: UpdateTaskDto): Promise<Task> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.patch<DoubleWrappedResponse<Task>>(`/workspaces/${workspaceId}/tasks/${id}`, data)
    return response.data.data.data
  },

  async archiveTask(id: string): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/tasks/${id}/archive`)
  },
}

export default taskService
